import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Star, Flag, Check, Trophy, Share2, Flame, Video, Award } from "lucide-react";
import api, { TIER_META } from "../lib/api";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import ShareCard from "../components/ShareCard";
import ReactionRecorder from "../components/ReactionRecorder";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const STATUS_LABEL = {
  queued: { t: "In queue", c: "#a1a1aa" },
  in_review: { t: "Under review", c: "#f59e0b" },
  roasted: { t: "Roasted", c: "#10b981" },
  rejected: { t: "Closed / refunded", c: "#71717a" },
};

function Roasts({ sub, onChange, onSelected, onReact, share, busyId }) {
  const [roasts, setRoasts] = useState([]);
  const [rating, setRating] = useState(0);
  const [selecting, setSelecting] = useState(null);
  const [aiVote, setAiVote] = useState(sub.ai_vote || null);
  const aiRoast = sub.ai_roast && sub.ai_roast.available ? sub.ai_roast : null;

  const load = useCallback(async () => {
    const res = await api.get("/roasts", { params: { submission_id: sub.id } });
    setRoasts(res.data);
  }, [sub.id]);
  useEffect(() => { load(); }, [load]);

  const select = async (roast) => {
    setSelecting(roast.id);
    try {
      const res = await api.post(`/submissions/${sub.id}/select-roast`, { roast_id: roast.id, rating: rating || undefined });
      toast.success("Winner picked — roaster gets paid! 💸");
      onChange();
      if (onSelected) onSelected(sub, roast, res.data.certificate);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSelecting(null); }
  };

  const close = async () => {
    try {
      await api.post(`/submissions/${sub.id}/close`, { reason: "none_selected" });
      toast("Closed with no selection — you've been refunded.");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const report = async (roastId) => {
    try {
      await api.post("/report", { target_type: "roast", target_id: roastId, reason: "user_report" });
      toast("Reported — pulled from view pending review.");
      load();
    } catch (e) { toast.error("Failed to report"); }
  };

  const selected = sub.selected_roast_id;
  const panelIds = sub.panel_roast_ids || null;

  // Sec 17 — Panel Verdict view (no single winner; all panelists already paid).
  if (sub.is_panel && panelIds) {
    const panelists = roasts.filter((r) => panelIds.includes(r.id));
    const panelItem = { kind: "panel", tier: sub.tier, roasts: panelists.map((r) => ({ content_text: r.content_text, roaster_handle: r.roaster_handle })) };
    return (
      <div className="mt-4 space-y-3" data-testid={`panel-view-${sub.id}`}>
        <div className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase text-brand"><span>🎤</span> Panel Verdict · {panelists.length} roasters</div>
        {panelists.map((r) => (
          <div key={r.id} className="rounded-sm border border-brand/30 bg-brand/5 p-4">
            <p className="text-zinc-100 leading-relaxed">"{r.content_text}"</p>
            <span className="text-xs text-brand mt-2 block">{r.roaster_handle || "@roaster"}</span>
          </div>
        ))}
        {panelists.length === 0 && <p className="text-sm text-zinc-500">Collecting your panel… roasts appear here as they land.</p>}
        {panelists.length > 0 && (
          <button data-testid={`share-panel-${sub.id}`} onClick={() => share(panelItem, "panel-" + sub.id)} disabled={busyId === "panel-" + sub.id}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand hover:bg-brand-hover text-black text-sm font-bold transition-colors disabled:opacity-50">
            <Share2 size={14} /> {busyId === "panel-" + sub.id ? "Preparing…" : "Share the panel"}
          </button>
        )}
      </div>
    );
  }

  const winnerRoast = selected ? roasts.find((r) => r.id === selected) : null;
  const doAiVote = async (choice) => {
    setAiVote(choice);
    try { await api.post(`/submissions/${sub.id}/ai-vote`, { choice }); } catch (e) {}
  };
  const aiVsItem = (winnerRoast && aiRoast) ? {
    kind: "aivs", tier: sub.tier, ai_text: aiRoast.content_text, human_text: winnerRoast.content_text,
    winner: aiVote || "human",
  } : null;

  return (
    <div className="mt-4 space-y-3">
      {roasts.length === 0 && <p className="text-sm text-zinc-500">No approved roasts yet. Hang tight.</p>}
      {!selected && roasts.length > 0 && sub.status !== "rejected" && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-400">Optional rating for the winner:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`rate-star-${n}`}>
              <Star size={18} className={n <= rating ? "fill-brand-accent text-brand-accent" : "text-zinc-600"} />
            </button>
          ))}
        </div>
      )}
      {roasts.map((r) => {
        const isWinner = selected === r.id;
        return (
          <div key={r.id} data-testid={`roast-${r.id}`}
            className={`rounded-sm border p-4 ${isWinner ? "border-tier-mild bg-tier-mild/10" : "border-white/10 bg-zinc-950/50"}`}>
            <p className="text-zinc-100 leading-relaxed">"{r.content_text}"</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-zinc-500">by {r.roaster_name || "Roaster"}</span>
              <div className="flex items-center gap-2">
                {isWinner ? (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-xs font-bold text-tier-mild"><Trophy size={13} /> Selected</span>
                    <button data-testid={`reaction-btn-${r.id}`} onClick={() => onReact(sub, r)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors">
                      <Video size={13} /> Reaction video
                    </button>
                  </div>
                ) : (!selected && sub.status !== "rejected") ? (
                  <>
                    <button data-testid={`report-roast-${r.id}`} onClick={() => report(r.id)} className="text-zinc-500 hover:text-brand"><Flag size={14} /></button>
                    <button data-testid={`select-roast-${r.id}`} onClick={() => select(r)} disabled={selecting}
                      className="px-4 py-1.5 rounded-full bg-brand hover:bg-brand-hover text-black text-xs font-bold transition-colors disabled:opacity-50">
                      Pick this one
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
      {!selected && roasts.length > 0 && sub.status !== "rejected" && (
        <button data-testid="close-submission-btn" onClick={close} className="text-sm text-zinc-500 hover:text-white underline">
          None of these — close & refund
        </button>
      )}

      {aiVsItem && (
        <div data-testid={`aivs-${sub.id}`} className="mt-4 rounded-sm border border-[#a855f7]/30 bg-[#a855f7]/5 p-4">
          <div className="text-sm font-bold tracking-widest uppercase text-[#a855f7] mb-3">🤖 AI vs 👥 Human</div>
          <div className="rounded-sm border border-white/10 bg-zinc-950/50 p-3 mb-2">
            <div className="text-[11px] font-bold text-[#a855f7] uppercase tracking-widest">AI roast</div>
            <p className="text-sm text-zinc-300 mt-1">"{aiRoast.content_text}"</p>
          </div>
          <div className="rounded-sm border border-tier-mild/30 bg-tier-mild/5 p-3">
            <div className="text-[11px] font-bold text-tier-mild uppercase tracking-widest">Human roast (your pick)</div>
            <p className="text-sm text-zinc-100 mt-1">"{winnerRoast.content_text}"</p>
          </div>
          <div className="mt-3">
            <div className="text-xs text-zinc-400 mb-2">Which one actually got you?</div>
            <div className="flex gap-2">
              <button data-testid={`aivote-ai-${sub.id}`} onClick={() => doAiVote("ai")}
                className={`flex-1 py-2 rounded-full text-sm font-bold border transition-colors ${aiVote === "ai" ? "border-[#a855f7] bg-[#a855f7]/20 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>🤖 AI</button>
              <button data-testid={`aivote-human-${sub.id}`} onClick={() => doAiVote("human")}
                className={`flex-1 py-2 rounded-full text-sm font-bold border transition-colors ${aiVote === "human" ? "border-tier-mild bg-tier-mild/20 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>👥 Human</button>
            </div>
          </div>
          {aiVote && (
            <button data-testid={`share-aivs-${sub.id}`} onClick={() => share(aiVsItem, "aivs-" + sub.id)} disabled={busyId === "aivs-" + sub.id}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-full bg-brand hover:bg-brand-hover text-black text-sm font-bold transition-colors disabled:opacity-50">
              <Share2 size={14} /> {busyId === "aivs-" + sub.id ? "Preparing…" : `Share "${aiVote === "human" ? "Humans won 💀" : "AI won 🤖"}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudge, setNudge] = useState(null); // { sub, roast, cert } after picking a winner
  const [reaction, setReaction] = useState(null); // reaction recorder item
  const [certs, setCerts] = useState([]);
  const [certMeta, setCertMeta] = useState(null);
  const [streak, setStreak] = useState(null);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  const reactionItem = (sub, roast) => ({
    tier: sub.tier, roast_text: roast.content_text,
    roaster_name: roast.roaster_name, roaster_handle: roast.roaster_handle, roaster_avatar: roast.roaster_avatar,
  });

  const certItem = (c) => ({
    kind: "certificate", tier: c.tier, submission_kind: c.submission_kind,
    roaster_handle: c.roaster_handle, roaster_name: c.roaster_name, roaster_avatar: c.roaster_avatar,
    roaster_rank: c.roaster_rank, roast_text: c.roast_text, submitter_name: c.submitter_name,
  });

  const survivalItem = certMeta ? {
    kind: "survival", score: certMeta.survival_score, label: certMeta.survival_label,
    count: certMeta.count, submitter_name: undefined,
  } : null;

  const streakItem = streak ? {
    kind: "streak", week_points: streak.this_week_points, week_count: streak.this_week_count,
    streak_weeks: streak.streak_weeks, score: streak.current_score, label: streak.label,
  } : null;

  const loadCerts = useCallback(async () => {
    try { const r = (await api.get("/submitter/certificates")).data; setCerts(r.certificates); setCertMeta(r); } catch (e) {}
    try { setStreak((await api.get("/submitter/streak")).data); } catch (e) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/submissions/mine");
      setSubs(res.data);
    } finally { setLoading(false); }
    loadCerts();
  }, [loadCerts]);
  useEffect(() => { load(); }, [load]);

  const togglePublish = async (sub) => {
    try {
      const res = await api.post(`/submissions/${sub.id}/publish`, { publish: !sub.published });
      toast.success(res.data.published ? "Published to the public feed!" : "Removed from feed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const nudgeItem = nudge ? { tier: nudge.sub.tier, roast_text: nudge.roast.content_text, roaster_name: nudge.roast.roaster_name, roaster_handle: nudge.roast.roaster_handle, roaster_avatar: nudge.roast.roaster_avatar, roaster_wins: (nudge.roast.roaster_wins || 0) + 1 } : null;

  const publishThen = async (thenShare) => {
    try {
      const res = await api.post(`/submissions/${nudge.sub.id}/publish`, { publish: true });
      toast.success("Published to the feed!");
      load();
      const item = { ...nudgeItem, feedId: res.data.feed_id };
      setNudge(null);
      if (thenShare) share(item, "nudge");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Your Inbox</h1>
      <p className="text-zinc-400 mt-2 mb-8">Review the roasts, pick your favourite, and the roaster gets paid.</p>

      {certMeta && certMeta.count > 0 && (
        <div data-testid="survival-score-panel" className="mb-8 rounded-sm border border-brand/30 bg-gradient-to-br from-brand/10 to-transparent p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand">RoastLit Survival Score</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-heading font-bold text-5xl" data-testid="survival-score-value">{certMeta.survival_score}</span>
                <span className="text-zinc-500 text-lg">/ 100</span>
                <span className="ml-2 text-sm font-bold uppercase tracking-widest text-brand-accent" data-testid="survival-label">{certMeta.survival_label}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">Survived {certMeta.count} roast{certMeta.count === 1 ? "" : "s"}. Take harder tiers to climb faster.</div>
            </div>
            <button data-testid="share-survival-btn" onClick={() => share(survivalItem, "survival")} disabled={busyId === "survival"}
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-sm px-4 py-2.5 transition-colors">
              <Share2 size={15} /> {busyId === "survival" ? "Preparing…" : "Share my score"}
            </button>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-brand rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, certMeta.survival_score)}%` }} />
          </div>
          {streak && (
            <div data-testid="streak-recap" className="mt-4 flex items-center justify-between gap-3 flex-wrap border-t border-white/10 pt-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5" data-testid="streak-weeks">
                  <Flame size={16} className="text-brand" />
                  <b>{streak.streak_weeks}</b>&nbsp;week{streak.streak_weeks === 1 ? "" : "s"} on a streak
                </span>
                <span className="text-zinc-400" data-testid="streak-week-points">
                  {streak.this_week_points > 0 ? <>climbed <b className="text-brand-accent">+{streak.this_week_points}</b> this week</> : "no climb yet this week"}
                </span>
              </div>
              <button data-testid="share-streak-btn" onClick={() => share(streakItem, "streak")} disabled={busyId === "streak"}
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand/40 hover:bg-brand/10 text-brand font-bold text-sm px-4 py-2 transition-colors">
                <Share2 size={14} /> {busyId === "streak" ? "Preparing…" : "Share this week & challenge a friend"}
              </button>
            </div>
          )}
        </div>
      )}

      {certs.length > 0 && (
        <div data-testid="certificates-strip" className="mb-8 rounded-sm border border-[#d4af37]/30 bg-[#d4af37]/5 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Award size={18} className="text-[#d4af37]" />
            <span className="text-sm font-bold tracking-widest uppercase text-[#d4af37]">Your certificates · {certs.length}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {certs.map((c) => {
              const meta = TIER_META[c.tier] || TIER_META.savage;
              return (
                <div key={c.id} data-testid={`cert-${c.id}`} className="shrink-0 rounded-sm border border-white/10 bg-zinc-950/60 p-3 w-56">
                  <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: meta.color }}>
                    {c.submission_kind === "high_stakes" ? "Official Verdict" : `Certified ${meta.label} Survivor`}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Certified by {c.roaster_id ? <a href={`/roaster/${c.roaster_id}`} className="text-[#d4af37] hover:underline">{c.roaster_handle}</a> : c.roaster_handle} · <span className="text-[#d4af37]">{c.roaster_rank}-tier</span></div>
                  <button data-testid={`share-cert-${c.id}`} onClick={() => share(certItem(c), c.id)} disabled={busyId === c.id}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-full bg-[#d4af37] hover:brightness-110 text-black text-xs font-bold transition disabled:opacity-60">
                    <Share2 size={12} /> {busyId === c.id ? "Preparing…" : "Share certificate"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div className="text-zinc-500 py-16 text-center">Loading…</div> :
        subs.length === 0 ? (
          <div className="rounded-sm border border-white/10 bg-zinc-900/40 p-12 text-center">
            <p className="text-zinc-400">No submissions yet. Head to Submit to get roasted.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {subs.map((s) => {
              const meta = TIER_META[s.tier];
              const st = STATUS_LABEL[s.status] || STATUS_LABEL.queued;
              const isOpen = open === s.id;
              return (
                <div key={s.id} data-testid={`inbox-sub-${s.id}`} className="rounded-sm border border-white/10 bg-zinc-900/60">
                  <button onClick={() => setOpen(isOpen ? null : s.id)} className="w-full flex items-center gap-4 p-5 text-left">
                    <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full shrink-0" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
                    <div className="flex-1 min-w-0">
                      {s.content_type === "image" ? <span className="text-sm text-zinc-400">[Image submission]</span> :
                        <p className="text-sm text-zinc-200 truncate">"{s.content}"</p>}
                    </div>
                    <span className="text-xs font-bold shrink-0" style={{ color: st.c }}>{st.t}</span>
                    {isOpen ? <ChevronUp size={18} className="text-zinc-500" /> : <ChevronDown size={18} className="text-zinc-500" />}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-white/10 pt-4">
                      {s.selected_roast_id && (
                        <button data-testid={`publish-btn-${s.id}`} onClick={() => togglePublish(s)}
                          className={`mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${s.published ? "bg-tier-mild/20 text-tier-mild" : "bg-brand text-black hover:bg-brand-hover"}`}>
                          {s.published ? <><Check size={15} /> Published to feed</> : <><Share2 size={15} /> Publish to public feed</>}
                        </button>
                      )}
                      <Roasts sub={s} onChange={load} onSelected={(sub, roast, cert) => setNudge({ sub, roast, cert })} onReact={(sub, roast) => setReaction(reactionItem(sub, roast))} share={share} busyId={busyId} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={!!nudge} onOpenChange={(o) => { if (!o) setNudge(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md" data-testid="publish-nudge">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl flex items-center gap-2">
              <Flame className="text-brand" size={22} /> Winner picked!
            </DialogTitle>
            <DialogDescription className="text-zinc-400">Publish it to the feed and drop it on your story — that's how new people find RoastLit.</DialogDescription>
          </DialogHeader>
          {nudgeItem && (
            <div className="flex justify-center py-1">
              <div style={{ width: 540 * 0.34, height: 960 * 0.34, overflow: "hidden", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)" }}>
                <div style={{ transform: "scale(0.34)", transformOrigin: "top left" }}><ShareCard item={nudgeItem} /></div>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            {nudge?.cert && (
              <button data-testid="nudge-share-cert" onClick={() => { const c = nudge.cert; setNudge(null); share(certItem(c), c.id); }}
                className="w-full py-3 rounded-full bg-[#d4af37] hover:brightness-110 text-black font-bold transition flex items-center justify-center gap-2">
                <Award size={16} /> 🏆 Share your certificate
              </button>
            )}
            <button data-testid="nudge-publish-share" onClick={() => publishThen(true)}
              className="w-full py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors flex items-center justify-center gap-2">
              <Share2 size={16} /> Publish &amp; Share to story
            </button>
            <button data-testid="nudge-reaction" onClick={() => { const r = reactionItem(nudge.sub, nudge.roast); setNudge(null); setReaction(r); }}
              className="w-full py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold transition-colors flex items-center justify-center gap-2">
              <Video size={16} /> 🎥 Record your reaction
            </button>
            <button data-testid="nudge-publish-only" onClick={() => publishThen(false)}
              className="w-full py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold transition-colors">
              Just publish to feed
            </button>
            <button data-testid="nudge-dismiss" onClick={() => setNudge(null)} className="text-xs text-zinc-500 hover:text-white py-1">Not now</button>
          </div>
        </DialogContent>
      </Dialog>
      {reaction && <ReactionRecorder open={!!reaction} item={reaction} onClose={() => setReaction(null)} />}
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
